import type { Amount, Currency } from './amount'
import type { Localization } from './localization'
import type { CustomConfig, Organization } from './organization'

export type ProjectId = string
export type ProjectRegionId = number
export type DateString = string
export type ProjectStatus =
  | 'draft'
  | 'validated'
  | 'not_public'
  | 'not_available'
  | 'archived'
  | 'pending'
export type ImpactSector = 'ecology' | 'social'
export type ImpactSectorColor =
  | 'purple'
  | 'green'
  | 'orange'
  | 'blue'
  | 'red'
  | 'pink'

export type Theme =
  | 'biodiversity'
  | 'circular_economy'
  | 'climate'
  | 'decontamination'
  | 'diversity'
  | 'education'
  | 'elderly'
  | 'food'
  | 'handicap'
  | 'insertion'
  | 'international_solidarity'
  | 'mental_health'
  | 'research'
  | 'sensibilization'
  | 'social_action'
  | 'women_rights'
  | 'youth'

export type MicroImpactIcon =
  | 'unknown'
  | 'agriculture'
  | 'aidkit'
  | 'art'
  | 'bee'
  | 'books'
  | 'books2'
  | 'call'
  | 'childbooks'
  | 'code'
  | 'cook'
  | 'coral'
  | 'diploma'
  | 'earth'
  | 'fish'
  | 'flocon'
  | 'florist'
  | 'food'
  | 'foot'
  | 'gamecards'
  | 'heart1'
  | 'heart2'
  | 'help'
  | 'home'
  | 'honey'
  | 'hygiene'
  | 'keys'
  | 'letter'
  | 'lgbtq'
  | 'lifebuoy'
  | 'liketonjob'
  | 'macbook'
  | 'meal'
  | 'medecine'
  | 'milk'
  | 'mpp'
  | 'music'
  | 'night'
  | 'noplastic'
  | 'officebag'
  | 'otherkit'
  | 'pencils'
  | 'periods'
  | 'price'
  | 'recycle'
  | 'scan'
  | 'schoolbag'
  | 'shooting'
  | 'soap'
  | 'solar'
  | 'stage'
  | 'suivi'
  | 'tchat'
  | 'tent'
  | 'therapeutic'
  | 'ticket'
  | 'toolbox'
  | 'tortle'
  | 'tree'
  | 'tshirt'
  | 'veggie'
  | 'water'
  | 'way'
  | 'world'

export const ODDIdList = [
  'poverty',
  'hunger',
  'health',
  'education',
  'gender_equality',
  'water',
  'energy',
  'economic_growth',
  'infrastructure',
  'inequality',
  'cities',
  'consumption',
  'climate',
  'marine',
  'land',
  'peace',
  'partnerships',
] as const

export type ODDId = (typeof ODDIdList)[number]

export type DeductionCategory =
  | 'general'
  | 'arup'
  | 'university'
  | 'corporate'
  | 'museum'
  | 'academia'
  | 'entrepreneurship'
  | 'religious'
  | 'festivals'
  | 'food'

export interface ProjectUpdate {
  id: string
  body: string
  created_at: DateString
  image_legend?: string
  image?: string
  name: string
  youtube_id?: string
  organization?: Organization
  // eslint-disable-next-line no-use-before-define
  project: Project
}

export interface ProjectAction {
  description: string
  description_singular?: string
  description_plural?: string
  description_v2: {
    base: string
    long?: {
      singular: string
      plural: string
    }
    short?: {
      singular: string
      plural: string
    }
  }
  cost: {
    amount: number
    currency: Currency
  }
  icon: MicroImpactIcon
  total?: number
  num_items: number
  cumulated_num_items?: number
}

export interface ProjectTheme {
  theme: Theme
  color: ImpactSectorColor
  impact_sector: ImpactSector
}

export interface ProjectThemeWithLabel extends ProjectTheme {
  label: string
}

export interface ProjectLabel {
  name: string
  logo: string
}
export interface ProjectKeyNumber {
  value: string
  unit?: string
  description: string
  highlights?: string[]
}

export const RegionsFrance = [
  'Auvergne-Rhône-Alpes',
  'Bourgogne-Franche-Comté',
  'Bretagne',
  'Centre-Val de Loire',
  'Corse',
  'Grand Est',
  'Hauts-de-France',
  'Île-de-France',
  'Normandie',
  'Nouvelle-Aquitaine',
  'Occitanie',
  'Pays de la Loire',
  "Provence-Alpes-Côte d'Azur",
  'France',
] as const

export const RegionsOutreMer = [
  'Guadeloupe',
  'Guyane',
  'La Réunion',
  'Martinique',
  'Mayotte',
  'Nouvelle-Calédonie',
  'Outre-Mer',
] as const

export const RegionsWorld = [
  'Afrique',
  'Amérique du Nord',
  'Amérique du Sud',
  'Asie',
  'Europe',
  'Moyen-Orient',
  'Océanie',
  'International',
] as const

export type RegionFrance = (typeof RegionsFrance)[number]

export type RegionWorld = (typeof RegionsWorld)[number]

export type RegionOutreMer = (typeof RegionsOutreMer)[number]

export type Region = RegionFrance | RegionWorld | RegionOutreMer

interface SourceOrganization {
  name: string
  logo: string
}

interface EngieCustomFields {
  engie_scale?: string
  engie_points_objective?: string
  engie_point_value?: number
  engie_category_code?: string
  engie_jira?: {
    issue_key: string
    issue_url: string
    issue_id: string
  }
}
type CustomFields = EngieCustomFields

export interface Project {
  action?: ProjectAction
  amount_collected_total?: Amount
  rolling_amount_collected_total?: Amount
  rolling_objective?: Amount
  amount_max?: Amount
  cc_note?: string
  contributors_count: number
  goal?: string
  id: ProjectId
  image?: string
  image2?: string
  image3?: string
  image4?: string
  image5?: string
  impact_sector?: ImpactSector
  name: string
  number_of_votes_in_campaign?: number
  objective_to_do?: string
  organization: Organization
  organization_name?: string
  pitch?: string
  localizations: Localization[]
  preview_image?: string
  problem_addressed?: string
  project_updates: ProjectUpdate[]
  key_numbers: ProjectKeyNumber[]
  cause_key_number?: ProjectKeyNumber
  recent_result?: string
  region_tag?: string
  region_tags?: Region[]
  slug: string
  status: ProjectStatus
  theme1: ProjectTheme
  theme2?: ProjectTheme
  use_of_the_money?: string
  is_emergency?: boolean
  is_certified?: boolean
  youtube_id?: string
  hello_asso_url?: string
  can_direct_donate?: boolean
  promo?: number
  is_done?: boolean
  odds: ODDId[]
  labels: ProjectLabel[]
  similar_projects: Pick<
    Project,
    | 'id'
    | 'name'
    | 'slug'
    | 'status'
    | 'organization'
    | 'goal'
    | 'impact_sector'
    | 'region_tag'
    | 'theme1'
    | 'theme2'
    | 'preview_image'
    | 'action'
  >[]
  some_comments: string[] | null
  comment_count: number
  icon: MicroImpactIcon | null
  source_organization: SourceOrganization
  provider_brand?: {
    name?: string
    note?: string
    logo?: string
  }
  deduction_category?: DeductionCategory
  bg_color_override: string
  donation_increase_percent?: number
  custom_config: CustomConfig
  custom_fields?: CustomFields
  has_invited_member?: boolean
}

export interface ProjectRegion {
  id: ProjectRegionId
  name: Region
  parent_region_id: ProjectRegionId | null
}

export interface ProjectFilters {
  themes: ProjectTheme[]
  regions: ProjectRegion[]
  odds?: ODDId[]
}
